import type {
	AudioUrlResponse,
	StoriesListResponse,
	StorySortOrder,
	Story,
	StoryDetail,
	StoryHistoryResponse,
	StoryVersion,
	SubmitResponse,
} from "../types";
import { request } from "./client";

export interface StoriesListParams {
	page?: number;
	limit?: number;
	sort?: StorySortOrder;
	search?: string;
	tag?: string;
}

export function listStories(
	params?: StoriesListParams,
): Promise<StoriesListResponse> {
	const qs = new URLSearchParams();
	if (params?.page) qs.set("page", String(params.page));
	if (params?.limit) qs.set("limit", String(params.limit));
	if (params?.sort) qs.set("sort", params.sort);
	if (params?.search) qs.set("search", params.search);
	if (params?.tag) qs.set("tag", params.tag);
	const query = qs.toString();
	return request(`/api/stories${query ? `?${query}` : ""}`);
}

export function createStory(title?: string): Promise<Story> {
	return request("/api/stories", {
		method: "POST",
		body: JSON.stringify({ title: title || null }),
	});
}

export function getStory(id: string): Promise<StoryDetail> {
	return request(`/api/stories/${id}`);
}

export function submitStory(id: string, text: string): Promise<SubmitResponse> {
	const body = new FormData();
	body.append("input_type", "text");
	body.append("text", text);
	return request<SubmitResponse>(`/api/stories/${id}/submit`, {
		method: "POST",
		body,
	});
}

export function submitStoryVoice(
	id: string,
	blob: Blob,
): Promise<SubmitResponse> {
	const body = new FormData();
	body.append("input_type", "voice");
	body.append("audio", blob, "recording.webm");
	return request<SubmitResponse>(`/api/stories/${id}/submit`, {
		method: "POST",
		body,
	});
}

export function updateStoryText(
	id: string,
	text: string,
): Promise<StoryVersion> {
	return request(`/api/stories/${id}/text`, {
		method: "PATCH",
		body: JSON.stringify({ text }),
	});
}

export function updateStoryTitle(
	id: string,
	title: string | null,
): Promise<Story> {
	return request(`/api/stories/${id}/title`, {
		method: "PATCH",
		body: JSON.stringify({ title }),
	});
}

export function markDone(id: string): Promise<Story> {
	return request(`/api/stories/${id}/done`, { method: "PATCH" });
}

export function restartStory(id: string): Promise<Story> {
	return request(`/api/stories/${id}/restart`, { method: "PATCH" });
}

export function retryStory(id: string): Promise<SubmitResponse> {
	return request(`/api/stories/${id}/retry`, { method: "POST" });
}

export function updateStoryTags(id: string, tags: string[]): Promise<Story> {
	return request(`/api/stories/${id}/tags`, {
		method: "PATCH",
		body: JSON.stringify({ tags }),
	});
}

export function getStoryHistory(id: string): Promise<StoryHistoryResponse> {
	return request(`/api/stories/${id}/history`);
}

export function getStoryAudio(id: string): Promise<AudioUrlResponse> {
	return request(`/api/stories/${id}/audio`);
}

export function uploadFinalRecording(
	id: string,
	blob: Blob,
): Promise<AudioUrlResponse> {
	const body = new FormData();
	body.append("audio", blob, "recording.webm");
	return request(`/api/stories/${id}/recording`, {
		method: "POST",
		body,
	});
}

export function getFinalRecordingUrl(id: string): Promise<AudioUrlResponse> {
	return request(`/api/stories/${id}/recording`);
}

export function deleteStory(id: string): Promise<void> {
	return request(`/api/stories/${id}`, { method: "DELETE" });
}
